'use client';

import Script from 'next/script';
import { useEffect, useReducer, useRef, useState } from 'react';
import { FormDialog } from './FormDialog';
import { FormContext } from './context';
import { sendGTMEvent } from '@next/third-parties/google';
interface HubspotFormScriptProviderProps {
  children: React.ReactNode;
}
export type FormsState = {
  forms: {
    id: string;
    isSubmitted: boolean;
    isRendered: boolean;
  }[];
};
type CreateFormAction = {
  type: 'create_form';
  id: string;
};
type SubmitFormAction = {
  type: 'submit_form';
  id: string;
};
type RenderFormAction = {
  type: 'render_form';
  id: string;
};
type Action = CreateFormAction | SubmitFormAction | RenderFormAction;
function reducer(state: FormsState, action: Action): FormsState {
  const i = state.forms.findIndex(({
    id
  }) => id === action.id);
  switch (action.type) {
    case 'create_form':
      if (i === -1) {
        return {
          ...state,
          forms: [...state.forms, {
            id: action.id,
            isSubmitted: false,
            isRendered: false
          }]
        };
      }
      return state;
    case 'submit_form':
      return {
        ...state,
        forms: [...state.forms.slice(0, i), {
          ...state.forms[i],
          isSubmitted: true
        }, ...state.forms.slice(i + 1)]
      };
    case 'render_form':
      return {
        ...state,
        forms: [...state.forms.slice(0, i), {
          ...state.forms[i],
          isRendered: true
        }, ...state.forms.slice(i + 1)]
      };
    default:
      return state;
  }
}
export function HubspotFormScriptProvider({
  children
}: HubspotFormScriptProviderProps) {
  const dialogsRef = useRef<Map<string, HTMLDialogElement> | null>(null);
  const [state, dispatch] = useReducer(reducer, {
    forms: []
  });
  const [scriptIsReady, setScriptIsReady] = useState(false);
  function getRefsMap() {
    if (!dialogsRef.current) {
      dialogsRef.current = new Map();
    }
    return dialogsRef.current;
  }
  function openForm(formId: string) {
    const map = getRefsMap();
    const formRef = map.get(formId);
    sendGTMEvent({
      event: 'form_dialog_opened',
      value: formId
    });
    formRef?.showModal();
  }
  function closeForm(formId: string) {
    const map = getRefsMap();
    const formRef = map.get(formId);
    //sendGTMEvent({ event: 'form_dialog_closed', value: formId })
    formRef?.close();
  }
  function createForm(formId: string) {
    if (state.forms.findIndex(({
      id
    }) => formId === id)) {
      dispatch({
        type: 'create_form',
        id: formId
      });
    }
  }
  function onFormSubmitted(formId: string) {
    sendGTMEvent({
      event: 'hubspot_form_submitted',
      value: formId,
      location: 'dialog'
    });
    dispatch({
      type: 'submit_form',
      id: formId
    });
  }
  useEffect(() => {
    if (scriptIsReady) {
      state.forms.forEach(form => {
        if (!form.isRendered) {
          //@ts-expect-error hbspt
          hbspt.forms.create({
            region: 'na1',
            portalId: '8035366',
            formId: form.id,
            target: `[data-hubspot-embed-form-id="${form.id}"]`,
            onFormSubmitted: () => {
              onFormSubmitted(form.id);
            }
          });
          dispatch({
            type: 'render_form',
            id: form.id
          });
        }
      });
    }
  }, [scriptIsReady, state.forms]);

  //log close events
  useEffect(() => {
    const map = getRefsMap();
    function onClose(id: string) {
      return () => {
        sendGTMEvent({
          event: 'form_dialog_closed',
          value: id
        });
      };
    }
    state.forms.forEach(({
      id
    }) => {
      const dialog = map.get(id);
      if (dialog) {
        dialog.addEventListener('close', onClose(id));
      }
    });
    return () => {
      state.forms.forEach(({
        id
      }) => {
        const dialog = map.get(id);
        if (dialog) {
          dialog.removeEventListener('close', onClose(id));
        }
      });
    };
  }, [state]);
  return <FormContext.Provider value={{
    createForm,
    openForm,
    closeForm,
    state
  }}>
      {children}
      {state.forms.map(({
      id
    }) => {
      return <FormDialog key={id} dialogRef={(node: HTMLDialogElement) => {
        const map = getRefsMap();
        if (node) {
          map.set(id, node);
        } else {
          map.delete(id);
        }
      }} closeDialog={closeForm} formId={id} />;
    })}
      <Script src="//js.hsforms.net/forms/embed/v2.js" id={`hubspot-form-script`} strategy="lazyOnload" onLoad={() => {
      setScriptIsReady(true);
    }} />
    </FormContext.Provider>;
}